import { create } from 'zustand';

/**
 * UI state store using Zustand
 * Replaces Angular's LoaderService, DialogService, and AlertService
 */

export type AlertSeverity = 'success' | 'error' | 'warning' | 'info';

export interface Alert {
  id: string;
  message: string;
  severity: AlertSeverity;
  autoHideDuration?: number;
}

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'error' | 'warning';
}

export interface PromptDialogOptions {
  title: string;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
}

interface UIState {
  // Global loader state
  isLoading: boolean;
  loadingMessage: string | null;

  // Sidebar state
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;

  // Alert/Toast notifications
  alerts: Alert[];

  // Confirm dialog state
  confirmDialog: {
    isOpen: boolean;
    options: ConfirmDialogOptions | null;
    resolve: ((confirmed: boolean) => void) | null;
  };

  // Prompt dialog state
  promptDialog: {
    isOpen: boolean;
    options: PromptDialogOptions | null;
    resolve: ((value: string | null) => void) | null;
  };

  // Modal state (for tracking open modals)
  openModals: string[];
}

interface UIActions {
  // Loader actions
  showLoader: (message?: string) => void;
  hideLoader: () => void;

  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;
  toggleSidebarCollapse: () => void;
  setSidebarCollapsed: (isCollapsed: boolean) => void;

  // Alert actions
  showAlert: (message: string, severity?: AlertSeverity, autoHideDuration?: number) => void;
  addAlert: (alert: { message: string; severity?: AlertSeverity }) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;

  // Confirm dialog actions
  showConfirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  closeConfirm: (confirmed: boolean) => void;

  // Prompt dialog actions
  showPrompt: (options: PromptDialogOptions) => Promise<string | null>;
  closePrompt: (value: string | null) => void;

  // Modal tracking
  openModal: (modalId: string) => void;
  closeModal: (modalId: string) => void;
  isModalOpen: (modalId: string) => boolean;
}

type UIStore = UIState & UIActions;

/**
 * Generate unique ID for alerts
 */
const generateAlertId = (): string => {
  return `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const useUIStore = create<UIStore>((set, get) => ({
  // Initial state
  isLoading: false,
  loadingMessage: null,
  isSidebarOpen: true,
  isSidebarCollapsed: false,
  alerts: [],
  confirmDialog: {
    isOpen: false,
    options: null,
    resolve: null,
  },
  promptDialog: {
    isOpen: false,
    options: null,
    resolve: null,
  },
  openModals: [],

  // Loader actions
  showLoader: (message?: string) => {
    set({ isLoading: true, loadingMessage: message || null });
  },

  hideLoader: () => {
    set({ isLoading: false, loadingMessage: null });
  },

  // Sidebar actions
  toggleSidebar: () => {
    set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
  },

  setSidebarOpen: (isOpen: boolean) => {
    set({ isSidebarOpen: isOpen });
  },

  toggleSidebarCollapse: () => {
    set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed }));
  },

  setSidebarCollapsed: (isCollapsed: boolean) => {
    set({ isSidebarCollapsed: isCollapsed });
  },

  // Alert actions
  showAlert: (message: string, severity: AlertSeverity = 'info', autoHideDuration: number = 5000) => {
    const alert: Alert = {
      id: generateAlertId(),
      message,
      severity,
      autoHideDuration,
    };

    set((state) => ({
      alerts: [...state.alerts, alert],
    }));

    // Auto-remove alert after duration
    if (autoHideDuration > 0) {
      setTimeout(() => {
        get().removeAlert(alert.id);
      }, autoHideDuration);
    }
  },

  // Alias for showAlert - used by hooks
  addAlert: (alertInput: { message: string; severity?: AlertSeverity }) => {
    get().showAlert(alertInput.message, alertInput.severity || 'info');
  },

  showSuccess: (message: string) => {
    get().showAlert(message, 'success');
  },

  showError: (message: string) => {
    get().showAlert(message, 'error', 7000); // Errors stay longer
  },

  showWarning: (message: string) => {
    get().showAlert(message, 'warning');
  },

  showInfo: (message: string) => {
    get().showAlert(message, 'info');
  },

  removeAlert: (id: string) => {
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.id !== id),
    }));
  },

  clearAlerts: () => {
    set({ alerts: [] });
  },

  // Confirm dialog actions
  showConfirm: (options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      set({
        confirmDialog: {
          isOpen: true,
          options: {
            confirmText: 'Confirm',
            cancelText: 'Cancel',
            confirmColor: 'primary',
            ...options,
          },
          resolve,
        },
      });
    });
  },

  closeConfirm: (confirmed: boolean) => {
    const { resolve } = get().confirmDialog;

    if (resolve) {
      resolve(confirmed);
    }

    set({
      confirmDialog: {
        isOpen: false,
        options: null,
        resolve: null,
      },
    });
  },

  // Prompt dialog actions
  showPrompt: (options: PromptDialogOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      set({
        promptDialog: {
          isOpen: true,
          options: {
            confirmText: 'OK',
            cancelText: 'Cancel',
            defaultValue: '',
            ...options,
          },
          resolve,
        },
      });
    });
  },

  closePrompt: (value: string | null) => {
    const { resolve } = get().promptDialog;
    if (resolve) resolve(value);
    set({
      promptDialog: { isOpen: false, options: null, resolve: null },
    });
  },

  // Modal tracking
  openModal: (modalId: string) => {
    set((state) => ({
      openModals: [...state.openModals.filter((id) => id !== modalId), modalId],
    }));
  },

  closeModal: (modalId: string) => {
    set((state) => ({
      openModals: state.openModals.filter((id) => id !== modalId),
    }));
  },

  isModalOpen: (modalId: string) => {
    return get().openModals.includes(modalId);
  },
}));

/**
 * Selector hooks for specific state slices
 */
export const useIsLoading = () => useUIStore((state) => state.isLoading);
export const useLoadingMessage = () => useUIStore((state) => state.loadingMessage);
export const useIsSidebarOpen = () => useUIStore((state) => state.isSidebarOpen);
export const useIsSidebarCollapsed = () => useUIStore((state) => state.isSidebarCollapsed);
export const useAlerts = () => useUIStore((state) => state.alerts);
export const useConfirmDialog = () => useUIStore((state) => state.confirmDialog);
export const usePromptDialog = () => useUIStore((state) => state.promptDialog);

export default useUIStore;
